import { t } from "@i18n";
import { DateTime } from "luxon";
import { showToastNotification } from "../../reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "../../services/constants";
import download from "downloadjs";
import { v4 as uuidv4 } from "uuid";
import axiosWrapper from "../../services/axiosWrapper";
import { assetTypeUrl, getSpacesList } from "@Services/dataManager";
import { getBackendUrl } from "@Utils";
import type { CheckExistingAssetIdsResponse, GetAssetTypesResponse } from "@ServerTypes/assets/assets.schema";
import type { GetSpacesListResponse } from "@ServerTypes/spaces/space.schema";
import * as XLSX from "xlsx";
import {
  isRevisionInSeries,
  getRevisionPosition,
  getEffectiveSequenceRules,
  type RevisionRuleViolation,
  type RevisionSequenceRules,
  type RevisionSequenceType,
} from "../../../../helpers/revisionPattern";
import { fetchRevisionSeries } from "../../features/files/components/FileVersion/revisionSeriesShared";
import { extractDocumentNumber } from "helpers/constants";
import type { FormTemplateField, QuestionField } from "@ServerTypes/forms/forms.schema";
import type { AppDispatch, RootState } from "@Reducers/index";

/**
 * A form-template document as returned by `forms/getTemplates` / `projects/getFormTemplate`.
 * SCHEMA GAP: `getTemplatesResponse.result` is `z.array(z.any())` server-side, so the three fields
 * the import flow actually reads are named here.
 */
export type ExcelFormTemplate = {
  _id: string;
  name: string;
  template?: FormTemplateField[];
};

/**
 * One raw sheet row after the chosen header row is applied: header text -> cell text. Both readers
 * (`XLSX.sheet_to_json` with `raw: false` and `parseCsvText`) yield strings for every cell.
 */
export type ExcelRow = Record<string, string>;

/**
 * A column the import expects. Base columns carry only `headerText`/`isMandatory`; columns derived
 * from a form question additionally carry the question's id and answer constraints.
 */
export type ExcelExpectedField = {
  headerText: string;
  isMandatory?: boolean;
  value?: string;
  customFieldId?: string;
  fieldType?: string;
  displayAs?: string;
  isProjectLevel?: boolean;
};

/**
 * `getRequiredFields` yields plain header strings for schedule/estimate/cost-database and field
 * descriptors for files/assets. Every consumer branches on `source` first, then reads one shape.
 */
export type ExcelRequiredField = ExcelExpectedField | string;

/** One option in the "match columns" dropdown. */
export type ExcelMatchOption = { name: string; value: string };

/** Sheet header text -> the canonical field name it was matched to. */
export type ExcelColumnMapper = Record<string, string>;

/** The File-Manager folder the import is running in, as `FileManagerNew` hands it to `ExcelImport`. */
export type ExcelImportCurrentData = {
  projectId?: string;
  hierarchyId?: string;
  parentId?: string;
  spaceId?: string;
  folderName?: string;
  filterPath?: string;
};

/**
 * A column descriptor in the intermediate (Syncfusion-era) shape that `ExcelImport` translates into
 * SlickGrid column definitions. `template` holds a formatter callback and is only tested for truth.
 */
export type ExcelGridColumn = {
  field?: string;
  headerText?: string;
  name?: string;
  width?: string;
  textAlign?: string;
  type?: string;
  isPrimaryKey?: boolean;
  isMandatory?: boolean;
  clipMode?: string;
  fieldType?: string;
  displayAs?: string;
  allowSorting?: boolean;
  allowFiltering?: boolean;
  template?: unknown;
};

/**
 * A row carried through validation: the raw sheet row plus the per-row verdict. Custom-field answers
 * are written back onto the row under the question's own label, so values widen past `string`.
 */
export type ExcelValidatedRow = Record<string, unknown> & {
  errors: string[];
  isValid: boolean;
};

/**
 * A row held in the import's `finalData` / error-grid state. The four sources each build a
 * differently shaped record and the grid reads them through dynamic column keys, so the rows are
 * carried as bags of dynamic keys and the few arithmetic reads assert their field inline.
 */
export type ExcelImportRow = Record<string, unknown>;

/**
 * Reads a cell held under a dynamic key. Both sheet readers (`XLSX.sheet_to_json` with `raw: false`
 * and `parseCsvText`) yield a string for every cell, so anything else is treated as an empty cell -
 * which is what the `value ? … : null` shapes below already did with it.
 */
const readText = (value: unknown): string => (typeof value === "string" ? value : "");

/** A purchase unit as `units/getPurchaseUnits` returns it — both fields are optional in the schema. */
export type PurchaseUnitOption = { _id?: string; name?: string };

/** One asset row assembled from the sheet, in the shape `assets/importAssetsFromExcel` accepts. */
type AssetImportRow = Record<string, unknown> & {
  assetTag?: string;
  assetName?: string;
  description: string;
  type: string;
  space: string;
  template: string;
  spaceId?: string;
  formTemplateId?: string;
  isValid: boolean;
  customFields: Record<string, unknown>;
  projectLevelCustomFields: Record<string, unknown>;
  errors: string[];
};

/** The `useState` setters the validators are handed from `ExcelImport`. */
type SetRows = (rows: ExcelImportRow[]) => void;
type SetFlag = (value: boolean) => void;

const predecessorDetailsRegex = /.*: (FS|SS|FF|SF)(?:\d+d)?/;

const DMY_DATE_FORMATS = [
  "d/M/yyyy",
  "dd/MM/yyyy",
  "d-M-yyyy",
  "dd-MM-yyyy",
  "d/M/yy",
  "dd/MM/yy",
  "d-M-yy",
  "dd-MM-yy",
];

const DMY_DATETIME_FORMATS = DMY_DATE_FORMATS.flatMap((d) => [
  `${d} H:mm`,
  `${d} HH:mm`,
  `${d} H:mm:ss`,
  `${d} HH:mm:ss`,
]);

export const parseCsvText = (text: string): string[][] =>
  text
    .split(/\r?\n/)
    .filter((line) => line.length > 0)
    .map((line) => line.split(","));

const tryParseFormats = (value: string, formats: string[]): number | null => {
  for (const fmt of formats) {
    const parsed = DateTime.fromFormat(value, fmt);
    if (parsed.isValid) return parsed.toMillis();
  }
  return null;
};

export const parseDateFieldValue = (value: unknown, displayAs?: string): number | null => {
  const trimmed = String(value).trim();
  if (displayAs === "dateAndTime") {
    return tryParseFormats(trimmed, DMY_DATETIME_FORMATS) ?? tryParseFormats(trimmed, DMY_DATE_FORMATS);
  }
  return tryParseFormats(trimmed, DMY_DATE_FORMATS);
};

export const formatDateFieldValue = (value: unknown, displayAs?: string): string => {
  if (value === null || value === undefined || value === "") return "";
  const millis = Number(value);
  if (Number.isNaN(millis)) return String(value);
  const dt = DateTime.fromMillis(millis);
  if (!dt.isValid) return String(value);
  return dt.toFormat(displayAs === "dateAndTime" ? "dd/MM/yyyy HH:mm:ss" : "dd/MM/yyyy");
};

const flattenProjectLevelFields = (projectLevelFields: FormTemplateField[]): QuestionField[] => {
  const fields: QuestionField[] = [];
  projectLevelFields.forEach((section) => {
    section.questionFields?.forEach((q) => {
      fields.push(q);
    });
  });
  return fields;
};

export const excelImportSources = {
  COST_DATABASE: "costDatabase",
  ESTIMATE: "estimate",
  SCHEDULE: "schedule",
  FILES: "files",
  ASSETS: "assets",
};

const containerFields = (
  source: string,
  extraFields: ExcelFormTemplate[],
  projectLevelFields?: FormTemplateField[],
): ExcelMatchOption[] => {
  const projectFields: ExcelMatchOption[] = [];
  const instanceFields: ExcelMatchOption[] = [];
  extraFields.forEach((form) => {
    form.template?.forEach((field) => {
      // KNOWN BUG, preserved verbatim: `questionFields` is optional on a template section, and a
      // section with none would throw here — the sibling readers in this file all use `?.`. Erased
      // assertions only; adding a guard would change behaviour.
      instanceFields.push(
        ...field.questionFields!.map((x) => ({ name: x.questionValue!, value: x.questionValue!.toLowerCase() })),
      );
    });
  });
  if (source === "assets" && projectLevelFields?.length) {
    flattenProjectLevelFields(projectLevelFields).forEach((q) => {
      projectFields.push({
        name: `P-${q.questionValue}`,
        value: `p-${q.questionValue!.toLowerCase()}`,
      });
    });
  }
  const baseFields = source == "assets" ? requiredFieldsForAssets : filesRequiredFields;
  const baseMapped = baseFields.map((x) => ({ name: x.headerText, value: x.value || x.headerText }));
  if (source === "assets" && projectFields.length) {
    const templateEntry = baseMapped.find((x) => x.value === "template");
    const baseWithoutTemplate = baseMapped.filter((x) => x.value !== "template");
    return [...baseWithoutTemplate, ...projectFields, ...(templateEntry ? [templateEntry] : []), ...instanceFields];
  }
  return [...baseMapped, ...projectFields, ...instanceFields];
};

export const getSampleFile = ({
  source,
  extraFields,
  projectLevelFields,
}: {
  source: string;
  extraFields: ExcelFormTemplate[];
  projectLevelFields?: FormTemplateField[];
}) => {
  if (source === excelImportSources.SCHEDULE) {
    download("/sample-template-schedules.xlsx");
  }
  if (source === excelImportSources.ESTIMATE) {
    download("/Estimates-sample-template.xlsx");
  }
  if (source === excelImportSources.COST_DATABASE) {
    download("/CostDatabase-sample-template.xlsx");
  }
  if (source === excelImportSources.ASSETS) {
    const projectHeaders: string[] = [];
    if (projectLevelFields?.length) {
      flattenProjectLevelFields(projectLevelFields).forEach((q) => {
        projectHeaders.push(`P-${q.questionValue}`);
      });
    }
    const baseWithoutTemplate = requiredFieldsForAssets.filter((f) => f.value !== "template").map((f) => f.headerText);
    const allHeaders = [...baseWithoutTemplate, ...projectHeaders, t("Template")];
    const exampleRow = [
      assetsExampleFields[0]!.assetTag,
      assetsExampleFields[0]!.assetName,
      assetsExampleFields[0]!.description,
      assetsExampleFields[0]!.type,
      assetsExampleFields[0]!.space,
      ...projectHeaders.map((header: string) => `${header.replace("P-", "")} 1`),
      assetsExampleFields[0]!.template,
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([allHeaders, exampleRow]);
    XLSX.utils.book_append_sheet(wb, ws, "Assets");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    download(new Blob([wbout], { type: "application/octet-stream" }), "Asset_Import_Template.xlsx");
  }
  if (source === excelImportSources.FILES) {
    const rows = containerFields(source, extraFields);
    const headerNames = rows.map((field) => field.name);
    const csvContent = `data:text/csv;charset=utf-8,${headerNames.join(",")}`;

    var encodedUri = encodeURI(csvContent);
    var link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "sample-containers.csv");
    document.body.appendChild(link);

    link.click();
  }
};

const scheduleExampleFields = [
  {
    customTaskId: "ST00810",
    TaskName: "Backfilling & Compaction",
    Duration: "53",
    Start: "9/2/23",
    Finish: "10/21/23",
    PredecessorDetails: "ST00800: SS17d,ST00150: SS",
    WBSPath: "ST01510.ST01790.ST01800",
  },
];

const estimateExampleFields = [
  {
    Code: "2.01",
    "HSN/SAC": "1000",
    Description: "Backfilling & Compaction",
    Quantity: "53",
    Unit: "Each Cut",
    Rate: "7",
    Amount: "371",
    Parent: "9",
  },
];

const costDatabaseExampleFields = [
  {
    Code: "2.01",
    "HSN/SAC": "1000",
    Description: "Backfilling & Compaction",
    Unit: "Each Cut",
    Rate: "7",
    Parent: "9",
  },
];

const assetsExampleFields = [
  {
    assetTag: "asset-102A",
    assetName: "Office Assets",
    description: "Materials Provided",
    type: "Machinery and Equipment",
    space: "Space 1",
    template: "Template 1",
  },
];

const containerExampleFields = [
  {
    "File Name": "DOC-MIS-001x",
    "File Format": "PDF",
    "Folder Name": "Final",
  },
  {
    "File Name": "DOC-MIS-002",
    "File Format": "XLSX",
    "Folder Name": "",
  },
  {
    "File Name": "DOC-MIS-001",
    "File Format": "RVT",
    "Folder Name": "Revit/ARC",
  },
];

const scheduleRequiredFields = [
  "customTaskId",
  "TaskName",
  "Duration",
  "Start",
  "Finish",
  "PredecessorDetails",
  "WBSPath",
];

const estimateRequiredFields = [
  "Code",
  "HSN/SAC",
  "Description",
  "Quantity",
  "Purchase Unit Name",
  "Rate",
  "Amount",
  "Parent",
];

const costDatabaseRequiredFields = ["Code", "HSN/SAC", "Description", "Unit", "Rate", "Parent"];

const requiredFieldsForAssets: ExcelExpectedField[] = [
  {
    headerText: t("Asset Tag"),
    isMandatory: true,
    value: "assetTag",
  },
  {
    headerText: t("Asset Name"),
    isMandatory: true,
    value: "assetName",
  },
  {
    headerText: t("Description"),
    isMandatory: false,
    value: "description",
  },
  {
    headerText: t("Type"),
    isMandatory: false,
    value: "type",
  },
  {
    headerText: t("Space"),
    isMandatory: false,
    value: "space",
  },
  {
    headerText: t("Template"),
    isMandatory: false,
    value: "template",
  },
];

const filesRequiredFields: ExcelExpectedField[] = [
  {
    headerText: t("File Name"),
    isMandatory: true,
  },
  {
    headerText: t("File Format"),
    isMandatory: true,
  },
  {
    headerText: t("Folder Name"),
    isMandatory: false,
  },
  {
    headerText: t("Revision"),
    isMandatory: false,
  },
];

export const getRequiredFields = ({
  source,
  extraFields,
  projectLevelFields,
}: {
  source: string;
  extraFields: ExcelFormTemplate[];
  projectLevelFields?: FormTemplateField[];
}): ExcelRequiredField[] | undefined => {
  if (source === excelImportSources.SCHEDULE) {
    return scheduleRequiredFields;
  }
  if (source === excelImportSources.ESTIMATE) {
    return estimateRequiredFields;
  }
  if (source === excelImportSources.COST_DATABASE) {
    return costDatabaseRequiredFields;
  }
  if ([excelImportSources.ASSETS, excelImportSources.FILES].includes(source)) {
    const baseColumns = source === excelImportSources.ASSETS ? requiredFieldsForAssets : filesRequiredFields;
    const customFields: ExcelExpectedField[] = [];
    extraFields.map((form) => {
      form.template?.map((field) => {
        // KNOWN BUG, preserved verbatim: `questionFields` is optional and unguarded here.
        customFields.push(
          ...field.questionFields!.map((x) => ({
            headerText: x.questionValue!,
            // `value` is not declared on a question; it arrives through the schema's catchall, so it
            // is checked rather than asserted.
            value: readText(x.value) || x.questionValue!.toLowerCase(),
            isMandatory: x.isMandatory,
            customFieldId: x._id,
            fieldType: x.fieldType,
            displayAs: x.displayAs,
          })),
        );
      });
    });
    const projectFields: ExcelExpectedField[] = [];
    if (source === excelImportSources.ASSETS && projectLevelFields?.length) {
      flattenProjectLevelFields(projectLevelFields).forEach((q) => {
        projectFields.push({
          headerText: `P-${q.questionValue}`,
          value: `p-${q.questionValue!.toLowerCase()}`,
          isMandatory: q.isMandatory || false,
          customFieldId: q._id,
          fieldType: q.fieldType,
          isProjectLevel: true,
        });
      });
    }
    if (source === excelImportSources.ASSETS && projectFields.length) {
      const templateCol = baseColumns.find((col) => col.value === "template");
      const baseWithoutTemplate = baseColumns.filter((col) => col.value !== "template");
      return [...baseWithoutTemplate, ...projectFields, ...(templateCol ? [templateCol] : []), ...customFields];
    }
    return [...baseColumns, ...projectFields, ...customFields];
  }
};

export const getExampleFields = ({
  source,
  projectLevelFields,
}: {
  source: string;
  projectLevelFields?: FormTemplateField[];
}): Record<string, string>[] | undefined => {
  if (source === excelImportSources.SCHEDULE) {
    return scheduleExampleFields;
  }
  if (source === excelImportSources.ESTIMATE) {
    return estimateExampleFields;
  }
  if (source === excelImportSources.COST_DATABASE) {
    return costDatabaseExampleFields;
  }
  if (source == excelImportSources.FILES) {
    return containerExampleFields;
  }
  if (source == excelImportSources.ASSETS) {
    if (projectLevelFields?.length) {
      const example: Record<string, string> = { ...assetsExampleFields[0]! };
      flattenProjectLevelFields(projectLevelFields).forEach((q) => {
        example[`p-${q.questionValue!.toLowerCase()}`] = `${q.questionValue} 1`;
      });
      return [example];
    }
    return assetsExampleFields;
  }
};

export const wbsPathTypeDatasource = [
  {
    name: "Direct Parent",
    index: 0,
    type: "Parent names Contains single id for parent relation",
  },
  {
    name: 'Chain of Parent("." separated)',
    index: 1,
    type: "Parent names Contains (.) separated chain of ids for parent relation",
  },
  {
    name: 'Chain of Parent("," separated)',
    index: 2,
    type: "Parent names Contains (,) separated chain of ids for parent relation",
  },
];

export const dateTypeDatasource = [
  { name: "dd/mm/yy", value: "dd/LL/yy" },
  { name: "mm/dd/yy", value: "LL/dd/yy" },
  { name: "m/d/yy", value: "L/d/yy" },
  { name: "yy/mm/dd", value: "yy/LL/dd" },
  { name: "dd/mm/yyy hh:mm:ss AM/PM", value: "dd/LL/yyyy HH:mm" },
];

export const scheduleDropDownDataSource = [
  { name: "TaskId", value: "customTaskId" },
  { name: "TaskName", value: "TaskName" },
  { name: "Duration", value: "Duration" },
  { name: "Start", value: "Start" },
  { name: "Finish", value: "Finish" },
  { name: "PredecessorDetails", value: "PredecessorDetails" },
  { name: "WBSPath", value: "WBSPath" },
];

const estimateDropDownDataSource = [
  { name: "Code", value: "code" },
  { name: "HSN/SAC", value: "HSN_SAC" },
  { name: "Description", value: "description" },
  { name: "Quantity", value: "quantity" },
  { name: "Unit", value: "unit" },
  { name: "Rate", value: "rate" },
  { name: "Amount", value: "amount" },
  { name: "Parent", value: "parent" },
];

const costDatabaseDropDownDataSource = [
  { name: "Code", value: "code" },
  { name: "HSN/SAC", value: "HSN_SAC" },
  { name: "Description", value: "description" },
  { name: "Unit", value: "unit" },
  { name: "Rate", value: "rate" },
  { name: "Parent", value: "parent" },
];

export const getMatchDropdownDataSource = ({
  source,
  customFields,
  projectLevelFields,
}: {
  source: string;
  customFields: ExcelFormTemplate[];
  projectLevelFields?: FormTemplateField[];
}): ExcelMatchOption[] | undefined => {
  if (source === excelImportSources.SCHEDULE) {
    return scheduleDropDownDataSource;
  }
  if (source === excelImportSources.ESTIMATE) {
    return estimateDropDownDataSource;
  }
  if (source === excelImportSources.COST_DATABASE) {
    return costDatabaseDropDownDataSource;
  }
  if ([excelImportSources.ASSETS, excelImportSources.FILES].includes(source)) {
    return containerFields(source, customFields, projectLevelFields);
  }
};

export const xmlDropdownDatasource = [{ text: "P6" }, { text: "MSP" }];
export const excludeAutoMatchingColumns = ["Start", "Finish", "WBSPath"];

export const getGridColumns = ({
  source,
  columnNames,
  allowCheckboxSelection,
  isValidateState,
  iconTemplate,
  errorTemplate,
  idTemplate,
}: {
  source: string;
  columnNames?: ExcelRequiredField[] | null;
  allowCheckboxSelection?: boolean;
  isValidateState?: boolean;
  iconTemplate?: unknown;
  errorTemplate?: unknown;
  idTemplate?: unknown;
}): ExcelGridColumn[] => {
  // Every branch of the chain below (including the final `else`) assigns, so no initialiser.
  let returnColumns: ExcelGridColumn[];
  if (source === excelImportSources.SCHEDULE) {
    returnColumns = [
      ...(isValidateState
        ? [
            {
              width: "40",
              textAlign: "Left",
              template: iconTemplate,
            },
          ]
        : []),
      {
        headerText: t("ID"),
        width: "155",
        textAlign: "Left",
        isPrimaryKey: true,
        template: idTemplate,
      },
      {
        field: "title",
        headerText: t("Title"),
        width: "155",
        textAlign: "Left",
      },
      {
        field: "pDetails",
        headerText: t("Predecessor"),
        width: "155",
        textAlign: "Left",
      },
      {
        field: "startDate",
        headerText: t("Start Date"),
        width: "155",
        textAlign: "Left",
        type: "date",
      },
      {
        field: "endDate",
        headerText: t("End Date"),
        width: "155",
        textAlign: "Left",
        type: "date",
      },
      ...(isValidateState
        ? [
            {
              field: "parentName",
              headerText: t("Parent"),
              width: "155",
              textAlign: "Left",
            },
          ]
        : []),
      {
        headerText: t("Duration"),
        field: "duration",
        width: "150",
        textAlign: "Left",
      },
    ];
  } else if (source === excelImportSources.ESTIMATE || source === excelImportSources.COST_DATABASE) {
    returnColumns = [
      ...(isValidateState
        ? [
            {
              width: "40",
              textAlign: "Left",
              template: iconTemplate,
            },
          ]
        : []),
      {
        field: "rowNo",
        headerText: t("RowNo"),
        width: "155",
        textAlign: "Left",
        isPrimaryKey: true,
      },
      {
        field: "code",
        headerText: t("Code"),
        width: "155",
        textAlign: "Left",
      },
      {
        field: "HSN_SAC",
        headerText: t("HSN/SAC"),
        width: "155",
        textAlign: "Left",
      },
      {
        field: "description",
        headerText: t("Description"),
        width: "155",
        textAlign: "Left",
      },
      ...(source === excelImportSources.ESTIMATE
        ? [
            {
              field: "quantity",
              headerText: t("Quantity"),
              width: "155",
              textAlign: "Left",
            },
          ]
        : []),
      {
        field: "unit",
        headerText: t("Unit"),
        width: "155",
        textAlign: "Left",
      },
      {
        field: "rate",
        headerText: t("Rate"),
        width: "155",
        textAlign: "Left",
      },
      ...(source === excelImportSources.ESTIMATE
        ? [
            {
              field: "amount",
              headerText: t("Amount"),
              width: "155",
              textAlign: "Left",
            },
          ]
        : []),
    ];
  } else if (source == excelImportSources.FILES) {
    returnColumns = [
      // For this source `columnNames` may be either the sheet's own header strings (Select Header /
      // Match Columns tabs) or the expected-field descriptors, so a bare header is widened into one.
      ...(columnNames || []).map((column) => {
        const expected: ExcelExpectedField = typeof column === "string" ? { headerText: column } : column;
        return {
          field: expected.headerText,
          headerText: expected.headerText,
          isMandatory: expected.isMandatory,
          clipMode: "Ellipsis",
          textAlign: "Left",
          fieldType: expected.fieldType,
          displayAs: expected.displayAs,
        };
      }),
      ...(isValidateState
        ? [
            {
              field: "errors",
              headerText: t("Errors"),
              width: "250",
              textAlign: "Left",
              clipMode: "Ellipsis",
              template: errorTemplate,
            },
          ]
        : []),
    ];
  } else if (source == excelImportSources.ASSETS) {
    returnColumns = [
      // Assets are always given the expected-field descriptors; a bare header string is widened into
      // one rather than asserted away.
      ...(columnNames || []).map((column) => {
        const expected: ExcelExpectedField = typeof column === "string" ? { headerText: column } : column;
        return {
          field: expected.value || expected.headerText.toLowerCase(),
          headerText: expected.headerText,
          isMandatory: expected.isMandatory,
          clipMode: "Ellipsis",
          textAlign: "Left",
        };
      }),
      ...(isValidateState
        ? [
            {
              field: "errors",
              headerText: t("Errors"),
              width: "250",
              textAlign: "Left",
              clipMode: "Ellipsis",
              template: errorTemplate,
            },
          ]
        : []),
    ];
  } else {
    returnColumns = [
      ...(allowCheckboxSelection
        ? [
            {
              type: "checkbox",
              allowSorting: false,
              allowFiltering: false,
              width: "50",
            },
          ]
        : []),
      ...(isValidateState
        ? [
            {
              width: "40",
              textAlign: "Left",
              template: iconTemplate,
            },
          ]
        : []),
      // KNOWN BUG, preserved verbatim: this branch dereferences `columnNames` with no `|| []`
      // fallback, unlike the two above.
      ...columnNames!.map((column) => {
        // These three sources pass plain header strings; a descriptor is read through its header.
        const headerText = typeof column === "string" ? column : column.headerText;
        return {
          field: headerText,
          headerText,
          clipMode: "Ellipsis",
          textAlign: "Left",
        };
      }),
    ];
  }

  return returnColumns;
};

export const validateScheduleData = ({
  sourceId,
  filteredJsonData,
  notValidPredecessors,
  setEnableNextButton,
  setScheduleIcorrectData,
  setIsScheduleDataValid,
  setFinalData,
  columnMapper,
  setButtonLoading,
  startDateTypeFormat,
  finishDateTypeFormat,
  wbsPathTypeIndex,
}: {
  sourceId?: string;
  filteredJsonData: ExcelRow[];
  notValidPredecessors: unknown[];
  setEnableNextButton: SetFlag;
  setScheduleIcorrectData: SetRows;
  setIsScheduleDataValid: SetFlag;
  setFinalData: SetRows;
  columnMapper: ExcelColumnMapper;
  setButtonLoading: SetFlag;
  startDateTypeFormat: string | null;
  finishDateTypeFormat: string | null;
  wbsPathTypeIndex: number | null;
}) => {
  const incorrectData: ExcelImportRow[] = [];
  function updateKeys(obj: ExcelRow, keyMapping: ExcelColumnMapper): ExcelRow {
    const updatedObject: ExcelRow = {};
    for (const key in obj) {
      updatedObject[keyMapping[key] || key] = obj[key]!;
    }
    return updatedObject;
  }
  // The same local carries the data through two shapes: first the sheet rows with their headers
  // renamed by `columnMapper`, then the assembled schedule tasks. Hence the dynamic-key view.
  let updatedJsonData: ExcelImportRow[] = filteredJsonData.map((obj) => updateKeys(obj, columnMapper));

  if (!notValidPredecessors.length) {
    updatedJsonData = updatedJsonData.map((data, index) => {
      if (data.Start) {
        if (typeof data.Start == "string") {
          // The Match Columns step gates Next on a format being picked, so it is set by now.
          const dateObject = DateTime.fromFormat(data.Start, startDateTypeFormat!);

          // `fromFormat` ALWAYS returns a DateTime - an unparseable date comes back with
          // `isValid: false` and NaN parts - so the object itself says nothing about the parse.
          if (dateObject.isValid) {
            const date = dateObject.day;
            const month = dateObject.month;
            const year = dateObject.year;
            data.Start = `${month}/${date}/${year}`;
          } else {
            incorrectData.push({
              ...data,
              id: data.customTaskId,
              column: "StartDate",
              type: "Start date format not valid",
              title: data.TaskName,
              predecessor: data.PredecessorDetails,
              startDate: data.Start,
            });
          }
        }
      }

      if (data.Finish) {
        if (typeof data.Finish == "string") {
          const dateObject = DateTime.fromFormat(data.Finish, finishDateTypeFormat!);
          // As above: only `isValid` distinguishes a parsed date from a failed one.
          if (dateObject.isValid) {
            const date = dateObject.day;
            const month = dateObject.month;
            const year = dateObject.year;
            data.Finish = `${month}/${date}/${year}`;
          } else {
            incorrectData.push({
              ...data,
              id: data.customTaskId,
              column: "FinishDate",
              type: "Finish date format not valid",
              title: data.TaskName,
              predecessor: data.PredecessorDetails,
              endDate: data.Finish,
            });
          }
        }
      }

      const wbsPath = readText(data.WBSPath);
      return {
        pDetails: data.PredecessorDetails ?? "",
        scheduleCounter: index + 1,
        mysqlId: uuidv4(),
        isDeleted: false,
        title: data.TaskName,
        tags: [],
        taskHasTasks: [],
        taskHasFiles: [],
        taskAssignedTo: [],
        taskHasIssues: [],
        taskHasResources: [],
        taskTypeId: null,
        taskTypeData: {},
        selectionSet: [],
        customTaskId: data.customTaskId ? readText(data.customTaskId).trim() : null,
        startDate: data.Start ? new Date(readText(data.Start)).getTime() : null,
        endDate: data.Finish ? new Date(readText(data.Finish)).getTime() : null,
        description: "",
        typeId: null,
        scheduleId: sourceId,
        hasChild: false,
        parentId: null,
        actualStartDate: null,
        actualEndDate: null,
        progress: "0",
        predecessor: "",
        parentName:
          wbsPathTypeIndex == 0
            ? wbsPath
              ? wbsPath.trim()
              : null
            : wbsPathTypeIndex == 1
              ? wbsPath
                ? wbsPath.split(".")[wbsPath.split(".").length - 1]!.trim()
                : null
              : wbsPath
                ? wbsPath.split(",")[wbsPath.split(",").length - 1]!.trim()
                : null,
        status: "",
        duration: data.Duration ?? 0,
      };
    });

    updatedJsonData.map((data) => {
      if (data.pDetails) {
        const individualPreDetails = readText(data.pDetails).split(/,(?!\s)/); // regex use for spliting the predecessors with ',' separated hours are occuring.

        individualPreDetails.map((data1: string) => {
          const validString = predecessorDetailsRegex.test(data1);
          if (!validString) {
            incorrectData.push({
              ...data,
              id: data.customTaskId,
              column: "predecessorDetails",
              type: "predecessor not valid",
              startDate: data.startDate
                ? typeof data.startDate == "string"
                  ? data.startDate
                  : new Date(Number(data.startDate))
                : null,
              endDate: data.endDate
                ? typeof data.endDate == "string"
                  ? data.endDate
                  : new Date(Number(data.endDate))
                : null,
            });
          }
        });
      }
    });

    const customTaskIdArray = updatedJsonData.map((data) => data.customTaskId);

    updatedJsonData.map((data) => {
      if (data.parentName && !customTaskIdArray.includes(readText(data.parentName).trim())) {
        incorrectData.push({
          ...data,
          id: data.customTaskId,
          column: "WBSPath",
          type: "parent not found",
          startDate: data.startDate
            ? typeof data.startDate == "string"
              ? data.startDate
              : new Date(Number(data.startDate))
            : null,
          endDate: data.endDate
            ? typeof data.endDate == "string"
              ? data.endDate
              : new Date(Number(data.endDate))
            : null,
        });
      }
    });
    const tempData: ExcelImportRow[] = [];
    updatedJsonData.map((data) => {
      updatedJsonData.map((data1) => {
        if (data.parentName == data1.customTaskId) {
          data.parentId = data1.mysqlId;
        }
      });
      tempData.push(data);
    });
    updatedJsonData = tempData;

    const temp = updatedJsonData.map((data) => {
      updatedJsonData.map((data1) => {
        if (data1.parentId == data.mysqlId) {
          data.hasChild = true;
        }
      });
      return data;
    });

    updatedJsonData = temp;

    const TaskIdAndScheduleCounterMapper: Record<string, unknown> = {};
    updatedJsonData.map((data) => {
      TaskIdAndScheduleCounterMapper[String(data.customTaskId)] = data.scheduleCounter;
    });

    const mySqlIdAndPredeDetailsMapper: Record<string, unknown> = {};
    updatedJsonData.map((data) => {
      // The uuid minted for this row above; it is this mapper's key.
      const mysqlId = String(data.mysqlId);
      const individualPredDetails = readText(data.pDetails).split(/,(?!\s)/); // regex use for spliting the predecessors with ',' separated hours are coming.

      // KNOWN BUG, preserved verbatim: this compares an array against "", which JS coercion makes
      // true only for an array whose text is empty — a row with no predecessors splits to `[""]`
      // and so takes the `else` arm below.
      if ((individualPredDetails as unknown) != "") {
        individualPredDetails.map((predecessor: string, index: number) => {
          let minutes = 0;

          const predPart = predecessor.split(":")[1];
          if (predPart) {
            if (predPart.includes("m")) {
              // minutes-only predecessor handled by default minutes = 0
            } else if (predPart.includes("h")) {
              if (predPart.includes(",")) {
                const a = predecessor.split(",");
                const hours = parseInt(a[1]!);
                const b = a[0]!.split(":");
                const days = parseInt(b[1]!.substring(3, predPart.length));
                minutes = days * 1440 + hours * 60;
              } else {
                const a = predecessor.split(":");
                const hours = parseInt(a[1]!.substring(3, predPart.length));
                minutes = hours * 60;
              }
            } else if (predPart.includes("d")) {
              const a = predecessor.split(":");
              const days = parseInt(a[1]!.substring(3, predPart.length));
              minutes = days * 1440;
            }
          }

          const predKey = predecessor.split(":")[0]!;
          const predSuffix = predecessor.split(":")[1];
          if (index == 0) {
            if (predSuffix && predSuffix.length > 3) {
              mySqlIdAndPredeDetailsMapper[mysqlId] = `${
                TaskIdAndScheduleCounterMapper[predKey]
              }${predSuffix.substring(0, 3)}+${minutes} minutes`;
            } else {
              mySqlIdAndPredeDetailsMapper[mysqlId] = `${
                TaskIdAndScheduleCounterMapper[predKey]
              }${predSuffix?.substring(0, 3) ?? ""}`;
            }
          } else if (predSuffix && predSuffix.length > 3) {
            mySqlIdAndPredeDetailsMapper[mysqlId] = `${mySqlIdAndPredeDetailsMapper[mysqlId]},${
              TaskIdAndScheduleCounterMapper[predKey]
            }${predSuffix.substring(0, 3)}+${minutes} minutes`;
          } else {
            mySqlIdAndPredeDetailsMapper[mysqlId] = `${mySqlIdAndPredeDetailsMapper[mysqlId]},${
              TaskIdAndScheduleCounterMapper[predKey]
            }${predSuffix?.substring(0, 3) ?? ""}`;
          }
        });
      } else {
        mySqlIdAndPredeDetailsMapper[mysqlId] = data.pDetails;
      }
    });

    updatedJsonData = updatedJsonData.map((data) => {
      const mysqlId = String(data.mysqlId);
      const dataToReturn: ExcelImportRow = {
        ...data,
        predecessor: mySqlIdAndPredeDetailsMapper[mysqlId] ?? "",
      };
      delete dataToReturn.parentName;
      delete dataToReturn.pDetails;
      return dataToReturn;
    });

    if (incorrectData.length) {
      setEnableNextButton(false);
      setScheduleIcorrectData(incorrectData);
      setIsScheduleDataValid(false);
    } else {
      setIsScheduleDataValid(true);
    }
    setFinalData(updatedJsonData);
    setButtonLoading(false);
  }
};

function checkValidationForEstimate({
  data,
  incorrectData,
}: {
  data: ExcelImportRow;
  incorrectData: ExcelImportRow[];
}) {
  if (data.code == "" && data.description == "") {
    incorrectData.push({
      ...data,
      row: data.rowNo,
      column: "description",
      type: "Description is mandatory for manual items",
    });
  }
  if (data.code == "" && data.quantity != null) {
    if (data.rate == null || Number(data.rate) < 0) {
      incorrectData.push({
        ...data,
        row: data.rowNo,
        column: "rate",
        type: "Rate is mandatory if quantity is present for manual item",
      });
    }
    if (data.purchaseUnit == "") {
      incorrectData.push({
        ...data,
        row: data.rowNo,
        column: "unit",
        type: "Valid Unit is mandatory if quantity is present for manual items",
      });
    }
  }
  if (data.code != "") {
    data.description = "";
    if (data.quantity == null) {
      data.rate = null;
      data.amount = "";
    }
    data.purchaseUnit = "";
  }
  if (data.parent == null) {
    data.level = 0;
  }
}

const RecordsWithCorrectParent = ({
  data,
  parentId = null,
}: {
  data: ExcelImportRow[];
  parentId?: unknown;
}): ExcelImportRow[] => {
  const records = data.filter((item) => item.parent == parentId);

  records.map((item) => {
    records.push(...RecordsWithCorrectParent({ data, parentId: item.rowNo }));
  });

  return records;
};

const parentValidatorForEstimateAndCostDatabase = ({
  arrayOfDataToSave,
  incorrectData,
}: {
  arrayOfDataToSave: ExcelImportRow[];
  incorrectData: ExcelImportRow[];
}) => {
  const validParentRecords = RecordsWithCorrectParent({
    data: arrayOfDataToSave,
  });
  arrayOfDataToSave.map((item) => {
    if (!validParentRecords.find((record) => record.rowNo == item.rowNo)) {
      incorrectData.push({
        ...item,
        row: item.rowNo,
        column: "parent",
        type: "Invalid Parent Hierarchy",
      });
    }
  });
};

function checkValidationForCostDatabase({
  data,
  incorrectData,
  arrayOfDataToSave,
}: {
  data: ExcelImportRow;
  incorrectData: ExcelImportRow[];
  arrayOfDataToSave: ExcelImportRow[];
}) {
  if (data.code && arrayOfDataToSave.find((item) => item.code == data.code)) {
    incorrectData.push({
      ...data,
      row: data.rowNo,
      column: "code",
      type: "Duplicate Code not allowed",
    });
  }
  if (data.code == "" || data.description == "") {
    incorrectData.push({
      ...data,
      row: data.rowNo,
      column: "code",
      type: "Code and Description is mandatory for items",
    });
  }
  if (data.rate !== null && Number(data.rate) < 0) {
    incorrectData.push({
      ...data,
      row: data.rowNo,
      column: "rate",
      type: "Rate must be non negative value",
    });
  }
  if (data.parent == null) {
    data.level = 0;
  }
}

export const MANDATORY_FIELDS_ERROR = "Mandatory Fields not filled";
const DUPLICATE_CONTAINER_ERROR = "Duplicate Containers Name Found.";
export const REVISION_NOT_IN_SERIES_ERROR = "Revision does not match the folder's Revision Series";
export const REVISION_MUST_BE_INITIAL_ERROR = "Revision must start at the series's initial value";
export const REVISION_MUST_BE_SAME_OR_NEXT_ERROR = "Revision must be same as or next after the current revision";
export const REVISION_MUST_ADVANCE_ERROR = "Revision must be later in the series than the previous revision";
export const REVISION_BACKWARDS_ERROR = "Upload of earlier revision is not allowed";
export const REVISION_REPEATED_ERROR = "Revision is already assigned to another version of this file";
export const REVISION_REQUIRED_ERROR = "Metadata is not available in the Document Register, file cannot be uploaded";
export const DOCUMENT_NUMBER_DUPLICATE_IN_IMPORT_ERROR =
  "Duplicate Document Number and file type in the imported file for the same folder";

export const REVISION_RULE_ERROR_MESSAGES: Record<RevisionRuleViolation, string> = {
  NOT_IN_SERIES: REVISION_NOT_IN_SERIES_ERROR,
  MUST_BE_INITIAL: REVISION_MUST_BE_INITIAL_ERROR,
  SEQUENTIAL_VIOLATION: REVISION_MUST_BE_SAME_OR_NEXT_ERROR,
  MUST_ADVANCE: REVISION_MUST_ADVANCE_ERROR,
  BACKWARDS_NOT_ALLOWED: REVISION_BACKWARDS_ERROR,
  REPEATED_REVISION: REVISION_REPEATED_ERROR,
  REVISION_REQUIRED: REVISION_REQUIRED_ERROR,
};
export const REVISION_ERROR_SET = new Set<string>(Object.values(REVISION_RULE_ERROR_MESSAGES));

function getFileKey(row: ExcelImportRow): string {
  const name = readText(row["File Name"]) || readText(row["file name"]);
  const ext = readText(row["File Format"]) || readText(row["file format"]);
  return (ext ? `${name}.${ext}` : name).toLowerCase();
}

export function invalidFieldError(questionValue: string): string {
  return `Custom Field input in '${questionValue}' is incorrect`;
}

export const saveContainers = async ({
  filteredJsonData,
  setFinalData,
  setButtonLoading,
  extraFields,
  setEnableNextButton,
  currentData,
  matchByDocumentNumber = false,
}: {
  filteredJsonData: ExcelRow[];
  setFinalData: SetRows;
  setButtonLoading: SetFlag;
  extraFields: ExcelFormTemplate[];
  setEnableNextButton: SetFlag;
  currentData?: ExcelImportCurrentData | null;
  matchByDocumentNumber?: boolean;
  /** Accepted from the caller but deliberately unread: container rows keep their sheet headers. */
  columnMapper?: ExcelColumnMapper;
}) => {
  const customFieldsMap: Record<string, QuestionField> = {};
  extraFields.forEach((form) => {
    form.template?.forEach((section) => {
      section.questionFields?.forEach((q) => {
        customFieldsMap[q.questionValue!.toLowerCase()] = q;
      });
    });
  });

  let folderSeriesId: string | null = null;
  let folderStartPattern: string | null = null;
  let folderSequenceType: RevisionSequenceType | null = null;
  let folderSequenceRules: RevisionSequenceRules | null = null;
  if (currentData?.hierarchyId) {
    const series = await fetchRevisionSeries(currentData.hierarchyId);
    folderSeriesId = series.revisionSeriesId;
    folderStartPattern = series.startPattern;
    folderSequenceType = series.sequenceType ?? null;
    folderSequenceRules = getEffectiveSequenceRules({
      defaultBehavior: series.defaultBehavior ?? undefined,
      sequenceRules: series.sequenceRules ?? undefined,
    });
  }

  const finalResult: ExcelValidatedRow[] = filteredJsonData.map((obj) => {
    const rowData: ExcelValidatedRow = { ...obj, errors: [], isValid: true };

    const addError = (msg: string) => {
      rowData.errors.push(msg);
      rowData.isValid = false;
    };

    const fileName = obj["File Name"] || obj["file name"] || "";
    const fileFormat = obj["File Format"] || obj["file format"] || "";
    if (!fileName || !fileFormat) addError(MANDATORY_FIELDS_ERROR);

    const revisionValue = String(obj["Revision"] ?? obj["revision"] ?? "").trim();
    if (folderStartPattern && folderSeriesId) {
      rowData.startPattern = folderStartPattern;
      rowData.sequenceType = folderSequenceType;
      rowData.sequenceRules = folderSequenceRules;
    }
    if (revisionValue && folderStartPattern && folderSeriesId) {
      if (isRevisionInSeries(revisionValue, folderStartPattern, folderSequenceType)) {
        rowData.revisionSeriesId = folderSeriesId;
      }
      if (getRevisionPosition(revisionValue, folderStartPattern, folderSequenceType) === null) {
        rowData.errors.push(REVISION_NOT_IN_SERIES_ERROR);
      }
    } else if (!revisionValue && folderSequenceRules?.revisionMandatory) {
      rowData.errors.push(REVISION_REQUIRED_ERROR);
    }

    for (const [fieldKey, qn] of Object.entries(customFieldsMap)) {
      const rawValue = obj[qn.questionValue!] ?? obj[fieldKey] ?? "";
      // `?? ""` above already turned an absent cell into the empty string.
      const hasValue = rawValue !== "";

      if (qn.isMandatory && !hasValue) {
        addError(MANDATORY_FIELDS_ERROR);
        continue;
      }

      if (!hasValue) continue;

      if (qn.fieldType === "single-select") {
        if (!qn.answerChoices?.includes(rawValue)) addError(invalidFieldError(qn.questionValue!));
      } else if (qn.fieldType === "multi-select") {
        const choices = String(rawValue)
          .split(",")
          .map((it: string) => it.trim())
          .filter((it: string) => qn.answerChoices?.includes(it));
        if (choices.length === 0) {
          addError(invalidFieldError(qn.questionValue!));
        } else {
          rowData[qn.questionValue!] = choices;
        }
      } else if (["single-line", "multi-line"].includes(qn.fieldType)) {
        const max = qn.maxFieldLimit || Infinity;
        const min = qn.minFieldLimit || 0;
        if (typeof rawValue === "string" && (rawValue.length < min || rawValue.length > max)) {
          addError(invalidFieldError(qn.questionValue!));
        }
      } else if (qn.fieldType === "number") {
        const num = Number(rawValue);
        const max = qn.maxFieldRange || Infinity;
        const min = qn.minFieldRange || -Infinity;
        if (isNaN(num) || num < min || num > max) {
          addError(invalidFieldError(qn.questionValue!));
        } else {
          rowData[qn.questionValue!] = num;
        }
      } else if (qn.fieldType === "date-time") {
        const parsed = parseDateFieldValue(String(rawValue), qn.displayAs);
        if (parsed === null) {
          addError(invalidFieldError(qn.questionValue!));
        } else {
          rowData[qn.questionValue!] = parsed;
        }
      }
    }

    return rowData;
  });

  const seen = new Map<string, number>();
  finalResult.forEach((row) => seen.set(getFileKey(row), (seen.get(getFileKey(row)) || 0) + 1));
  finalResult.forEach((row) => {
    if ((seen.get(getFileKey(row)) || 0) > 1) {
      row.errors.push(DUPLICATE_CONTAINER_ERROR);
      row.isValid = false;
    }
  });

  if (matchByDocumentNumber) {
    const currentFolderPath = [
      String(currentData?.filterPath ?? "").replace(/^\/+|\/+$/g, ""),
      String(currentData?.folderName ?? "").trim(),
    ]
      .filter(Boolean)
      .join("/");
    const normalizeFolder = (value: string): string => {
      const trimmed = value.trim().replace(/^\/+|\/+$/g, "");
      if (currentFolderPath && trimmed.startsWith(currentFolderPath)) {
        return trimmed.slice(currentFolderPath.length).replace(/^\/+/, "");
      }
      return trimmed;
    };
    const docNumberSeen = new Map<string, number>();
    const docNumberKeyOf = (row: ExcelValidatedRow): string | null => {
      const docNumber = extractDocumentNumber(row);
      if (!docNumber) return null;
      const folder = normalizeFolder(String(row["Folder Name"] ?? row["folder name"] ?? ""));
      const fileType = String(row["File Format"] ?? row["file format"] ?? "")
        .trim()
        .toLowerCase();
      return `${folder}|${docNumber.toLowerCase()}|${fileType}`;
    };
    finalResult.forEach((row) => {
      const key = docNumberKeyOf(row);
      if (key) docNumberSeen.set(key, (docNumberSeen.get(key) || 0) + 1);
    });
    finalResult.forEach((row) => {
      const key = docNumberKeyOf(row);
      if (key && (docNumberSeen.get(key) || 0) > 1) {
        if (!row.errors.includes(DOCUMENT_NUMBER_DUPLICATE_IN_IMPORT_ERROR)) {
          row.errors.push(DOCUMENT_NUMBER_DUPLICATE_IN_IMPORT_ERROR);
        }
        row.isValid = false;
      }
    });
  }

  setFinalData(finalResult);
  setEnableNextButton(true);
  setButtonLoading(false);
};

function updateKeys(obj: ExcelRow, keyMapping: ExcelColumnMapper): ExcelRow {
  const updatedObject: ExcelRow = {};
  for (const key in obj) {
    updatedObject[keyMapping[key] || key] = obj[key] || "";
  }
  return updatedObject;
}

export const validateAssetData = async ({
  sourceId,
  filteredJsonData,
  setFinalData,
  setEnableNextButton,
  setButtonLoading,
  extraFields,
  projectLevelFields,
  columnMapper,
}: {
  sourceId?: string;
  filteredJsonData: ExcelRow[];
  setFinalData: SetRows;
  setEnableNextButton: SetFlag;
  setButtonLoading: SetFlag;
  extraFields: ExcelFormTemplate[];
  projectLevelFields?: FormTemplateField[];
  columnMapper: ExcelColumnMapper;
}) => {
  const spaceList = await axiosWrapper.post<GetSpacesListResponse>(getSpacesList, { projectId: sourceId });
  const updatedJsonData = filteredJsonData.map((obj) => updateKeys(obj, columnMapper));

  const assetIdsToCheck = updatedJsonData
    .map((item) => item[columnMapper["Asset Tag"]!])
    .filter((id) => id && id.trim());

  const existingAssetIdsResponse = await axiosWrapper.post<CheckExistingAssetIdsResponse>(
    `${getBackendUrl()}/assets/checkExistingAssetIds`,
    {
      assetIds: assetIdsToCheck,
      projectId: sourceId,
    },
  );
  const existingAssetIds = existingAssetIdsResponse.data;

  const spaceMapper: Record<string, string> = {};
  let defaultSpaceId: string | null = null;

  const spaces = spaceList.data.status ? spaceList.data.data : undefined;
  if (Array.isArray(spaces)) {
    for (const space of spaces) {
      if (space.isDefault) {
        defaultSpaceId = space.id;
      }
      spaceMapper[space.spaceName.toLowerCase()] = space.id;
    }
  }

  /** Templates keyed by lower-cased name, with every section's questions flattened into one list. */
  const formsMapper: Record<string, { _id: string; questionFields: QuestionField[] }> = {};
  extraFields.forEach((form) => {
    const key = form.name.toLowerCase();
    formsMapper[key] = {
      _id: form._id,
      questionFields: [],
    };
    // KNOWN BUG, preserved verbatim: `template` (optional) and `questionFields` are read unguarded,
    // so a template with no sections throws here.
    form.template!.forEach((section) => {
      section.questionFields!.forEach((question) => {
        formsMapper[key]!.questionFields.push(question);
      });
    });
  });

  const projectFieldsMap: Record<string, QuestionField> = {};
  if (projectLevelFields?.length) {
    projectLevelFields.forEach((section) => {
      section.questionFields?.forEach((q) => {
        projectFieldsMap[`p-${q.questionValue!.toLowerCase()}`] = q;
      });
    });
  }

  const allAssetTypes = await axiosWrapper.post<GetAssetTypesResponse>(assetTypeUrl, {
    value: { projectId: sourceId },
  });
  const availableAssetTypes = (allAssetTypes.data.result ?? []).map((item) => item.name.toLowerCase());
  const finalResult: ExcelImportRow[] = [];
  const mappedFieldValues = new Set(Object.values(columnMapper).map((v) => String(v).toLowerCase()));

  updatedJsonData.forEach((item) => {
    // A row with no Space/Template column has `undefined` here; the lookup is left unguarded as-is.
    const resolvedSpaceId = spaceMapper[item.space?.toLowerCase()!] || defaultSpaceId;
    const resolvedFormTemplateId = formsMapper[item.template?.toLowerCase()!]?._id;
    const rowData: AssetImportRow = {
      assetTag: item.assetTag,
      assetName: item.assetName,
      description: item.description || "",
      type: item.type?.toLowerCase() || "",
      space: item.space || "",
      template: item.template || "",
      spaceId: resolvedSpaceId || undefined,
      formTemplateId: resolvedFormTemplateId || undefined,
      isValid: true,
      customFields: {},
      projectLevelCustomFields: {},
      errors: [],
    };
    if (rowData.template) {
      const template = rowData.template.toLowerCase();
      const templateConfig = formsMapper[template];
      if (templateConfig && templateConfig.questionFields.length) {
        templateConfig.questionFields.map((qn) => {
          const fieldKey = qn.questionValue!.toLowerCase();
          const fieldInExcel = mappedFieldValues.has(fieldKey);
          const ans = item[fieldKey];
          if (qn.isMandatory && !ans) {
            rowData.errors.push("Mandatory fields in Custom Fields not filled");
            rowData.isValid = false;
            return;
          }
          if (!fieldInExcel && !ans) return;
          if (!ans || !rowData.isValid) return;
          let validAns: unknown = ans;
          if (["single-line", "multi-line"].includes(qn.fieldType)) {
            const max = qn.maxFieldLimit || Infinity;
            const min = qn.minFieldLimit || 0;
            rowData.isValid = ans.length >= min && ans.length <= max;
          } else if (qn.fieldType == "single-select") {
            // KNOWN BUG, preserved verbatim: `answerChoices` is optional and read unguarded here,
            // unlike the project-level loop below which uses `?.`. Erased assertions only.
            rowData.isValid = qn.answerChoices!.includes(ans);
          } else if (qn.fieldType == "multi-select") {
            const choices = ans
              .split(",")
              .map((it: string) => it.trim())
              .filter((it: string) => qn.answerChoices!.includes(it));
            rowData.isValid = choices.length > 0;
            validAns = choices;
          } else if (qn.fieldType == "number") {
            const num = Number(ans);
            const max = qn.maxFieldRange || Infinity;
            const min = qn.minFieldRange || -Infinity;
            rowData.isValid = typeof num == "number" && num >= min && num <= max;
            validAns = num;
          } else {
            const date = new Date(ans);
            rowData.isValid = !isNaN(date.getTime());
            validAns = rowData.isValid ? date.getTime() : null;
          }
          rowData.customFields[qn._id] = validAns;
          rowData[qn.questionValue!.toLowerCase()] = ans;
          if (!rowData.isValid) rowData.errors.push("Custom Fields data not valid");
        });
      } else {
        rowData.errors.push("Template does not exist");
      }
    }

    Object.entries(projectFieldsMap).forEach(([key, qn]) => {
      const ans = item[key];
      if (qn.isMandatory && !ans) {
        rowData.errors.push(`Project-level field "${qn.questionValue}" is mandatory`);
        rowData.isValid = false;
        return;
      }
      if (!ans) return;
      let validAns: unknown = ans;
      if (["single-line", "multi-line", "look-up"].includes(qn.fieldType)) {
        const max = qn.maxFieldLimit || Infinity;
        const min = qn.minFieldLimit || 0;
        if (typeof ans === "string" && (ans.length < min || ans.length > max)) {
          rowData.isValid = false;
          rowData.errors.push(`Project-level field "${qn.questionValue}" data not valid`);
        }
      } else if (qn.fieldType === "single-select") {
        if (!qn.answerChoices?.includes(ans)) {
          rowData.isValid = false;
          rowData.errors.push(`Project-level field "${qn.questionValue}" data not valid`);
        }
      } else if (qn.fieldType === "multi-select") {
        const choices = String(ans)
          .split(",")
          .map((it: string) => it.trim())
          .filter((it: string) => qn.answerChoices?.includes(it));
        if (choices.length === 0) {
          rowData.isValid = false;
          rowData.errors.push(`Project-level field "${qn.questionValue}" data not valid`);
        }
        validAns = choices;
      } else if (qn.fieldType === "number") {
        const num = Number(ans);
        const max = qn.maxFieldRange || Infinity;
        const min = qn.minFieldRange || -Infinity;
        if (isNaN(num) || num < min || num > max) {
          rowData.isValid = false;
          rowData.errors.push(`Project-level field "${qn.questionValue}" data not valid`);
        }
        validAns = num;
      } else if (qn.fieldType === "date-time") {
        const date = new Date(ans);
        if (isNaN(date.getTime())) {
          rowData.isValid = false;
          rowData.errors.push(`Project-level field "${qn.questionValue}" data not valid`);
        }
        validAns = !isNaN(date.getTime()) ? date.getTime() : null;
      }
      rowData.projectLevelCustomFields[qn._id] = validAns;
      rowData[`p-${qn.questionValue!.toLowerCase()}`] = ans;
    });

    if (!rowData.assetTag || !rowData.assetName) {
      rowData.errors.push("Mandatory Fields not filled");
      rowData.isValid = false;
    }
    if (rowData.assetTag) {
      const exists = existingAssetIds.includes(rowData.assetTag.toLowerCase());
      if (exists) {
        rowData.errors.push("Two or more Assets with same Tags exist");
        rowData.isValid = false;
      } else {
        existingAssetIds.push(rowData.assetTag.toLowerCase());
      }
    }
    if (!rowData.type || (rowData.type && !availableAssetTypes.includes(rowData.type.toLowerCase()))) {
      rowData.errors.push("Type does not exist");
      rowData.isValid = false;
    }
    if (rowData.space && !spaceMapper[rowData.space.toLowerCase()]) {
      rowData.errors.push("Space does not exist");
    }
    finalResult.push(rowData);
  });

  setFinalData(finalResult);
  setButtonLoading(false);
  if (finalResult.length) {
    setEnableNextButton(true);
  }
};

export const validateEstimateData = async ({
  source,
  sourceId,
  headerIndex,
  estimateData,
  filteredJsonData,
  purchaseUnitsList,
  setEstimateIcorrectData,
  setIsEstimateDataValid,
  setButtonLoading,
  setEnableNextButton,
  setFinalData,
  columnMapper,
  dispatch,
}: {
  source: string;
  sourceId?: string;
  headerIndex: number | null;
  estimateData: RootState["estimateData"];
  filteredJsonData: ExcelRow[];
  purchaseUnitsList: PurchaseUnitOption[];
  setEstimateIcorrectData: SetRows;
  setIsEstimateDataValid: SetFlag;
  setButtonLoading: SetFlag;
  setEnableNextButton: SetFlag;
  setFinalData: SetRows;
  columnMapper: ExcelColumnMapper;
  dispatch: AppDispatch;
}) => {
  const incorrectData: ExcelImportRow[] = [];
  const arrayOfDataToSave: ExcelImportRow[] = [];
  const updatedJsonData = filteredJsonData.map((obj) => updateKeys(obj, columnMapper));

  if (updatedJsonData.length < 1) {
    dispatch(
      showToastNotification({
        content: t("Data Missing"),
        type: TOAST_TYPE_ERROR,
      }),
    );
  }

  updatedJsonData.map((objectData, index) => {
    // `headerIndex` is set by the Select Header step before this validator can run.
    const data: ExcelImportRow = {
      rowNo: headerIndex! + index + 2,
    };
    if (source === excelImportSources.ESTIMATE) {
      data.estimateId = sourceId;
      // SCHEMA GAP: `getEstimateData` returns the linked cost database's `id`, but the response
      // schema declares only `currency` under `costDatabase`. Read through an `in` check rather than
      // asserting the member onto the declared shape.
      const costDatabase: unknown = estimateData.costDatabase;
      data.costDatabaseId =
        typeof costDatabase === "object" && costDatabase !== null && "id" in costDatabase ? costDatabase.id : undefined;
    } else {
      data.costDatabaseId = sourceId;
    }
    Object.keys(objectData).map((key: string) => {
      if (key == "unit") {
        if (objectData[key]?.trim()) {
          const currentRecordUnit = purchaseUnitsList.find((item) => objectData[key]?.trim() == item.name);
          if (currentRecordUnit) {
            data.purchaseUnit = currentRecordUnit._id;
            data.unit = currentRecordUnit.name;
          } else {
            incorrectData.push({
              ...data,
              row: data.rowNo,
              column: "unit",
              type: "Invalid Unit",
            });
          }
        } else {
          data.purchaseUnit = "";
        }
      } else if (key == "quantity") {
        if (Number.isFinite(parseFloat(objectData[key]! && objectData[key]!.replace(/,/g, "")))) {
          data.quantity = parseFloat(objectData[key]!.replace(/,/g, ""));
        } else {
          data.quantity = null;
        }
      } else if (key == "rate") {
        if (Number.isFinite(parseFloat(objectData[key]! && objectData[key]!.replace(/,/g, "")))) {
          data.rate = parseFloat(objectData[key]!.replace(/,/g, ""));
        } else {
          data.rate = null;
        }
      } else if (key == "amount") {
        // `rate`/`quantity` are still the raw sheet text here (this pass has not written the parsed
        // numbers back onto `objectData`), so the `Number.isFinite` arms are dead in practice.
        const rate = Number.isFinite(objectData.rate)
          ? objectData.rate
          : parseFloat(objectData.rate! && objectData.rate!.replace(/,/g, ""));
        const quantity = Number.isFinite(objectData.quantity)
          ? objectData.quantity
          : parseFloat(objectData.quantity! && objectData.quantity!.replace(/,/g, ""));
        if (rate && quantity) {
          // Both arms above can still hold the raw sheet text, which `*` would coerce anyway.
          data.amount = Number(rate) * Number(quantity);
        } else {
          data.amount = "";
        }
      } else if (key == "parent") {
        if (objectData[key] == "") {
          data.parent = null;
        } else if (Number.isInteger(+objectData[key]!)) {
          data.parent = +objectData[key]!;
        }
      } else {
        data[key] = objectData[key];
      }
    });

    source === excelImportSources.ESTIMATE
      ? checkValidationForEstimate({ data, incorrectData })
      : checkValidationForCostDatabase({
          data,
          incorrectData,
          arrayOfDataToSave,
        });

    arrayOfDataToSave.push(data);
  });

  parentValidatorForEstimateAndCostDatabase({
    arrayOfDataToSave,
    incorrectData,
  });
  const estimateFinalData = getLevelForEachRow(arrayOfDataToSave);
  if (typeof estimateFinalData == "object") {
    if (incorrectData.length) {
      setEstimateIcorrectData(incorrectData);
      setIsEstimateDataValid(false);
      setEnableNextButton(false);
    } else {
      setIsEstimateDataValid(true);
      setEnableNextButton(true);
    }
    setButtonLoading(false);
    setFinalData(estimateFinalData);
  }
};

function getLevelForEachRow(arr: ExcelImportRow[], parent: unknown = null, level = 0): ExcelImportRow[] {
  const result: ExcelImportRow[] = [];
  arr.forEach((item) => {
    if (item.parent === parent) {
      result.push({
        ...item,
        level,
      });
      result.push(...getLevelForEachRow(arr, item.rowNo, level + 1));
    }
  });
  return result;
}
